import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { amb } from './audio/AmbDirector';
import { AudioEngine } from './audio/AudioEngine';
import { cues } from './audio/Cues';
import { Clock } from './core/Clock';
import { applyQuality, resolveQuality, type QualitySettings } from './core/Quality';
import { InputMap } from './input/InputMap';
import { CharacterMotor } from './physics/CharacterMotor';
import { PhysicsWorld } from './physics/PhysicsWorld';
import { applyCameraBoom, BOOM_RADIUS } from './player/CameraBoom';
import { Locomotion } from './player/Locomotion';
import { PlayerVisual } from './player/PlayerVisual';
import { DayClock, PLAY_S_PER_ISLAND_H } from './survival/DayClock';
import { deathCopy, respawnAt, REVIVE_HP, REVIVE_HUNGER, REVIVE_THIRST } from './survival/Death';
import { Vitals } from './survival/Vitals';
import { Pulse } from './systems/Pulse';
import { SpawnDirector } from './ai/SpawnDirector';
import type { DinoActor, FaunaSense } from './dinos/DinoActor';
import { TRIKE_KIT } from './dinos/kits';
import { Axe } from './combat/Axe';
import { Bow, craftBow } from './combat/Bow';
import { Sling } from './combat/Sling';
import { Spear } from './combat/Spear';
import { PickupDirector } from './interact/Pickups';
import { DROWN_DELAY_S, DROWN_HP_PER_S } from './player/Swim';
import { meatYield } from './systems/Cook';
import { craftTorch, nearestTracked, TORCH_LAMP_BOOST, ToolDirector } from './tools/Tools';
import { AUTO_SAVE_S, SaveGame, type SaveV1 } from './save/SaveGame';
import { Ending } from './systems/Ending';
import { CampDirector } from './systems/Shelter';
import { DeathVeil } from './ui/DeathVeil';
import { Hud } from './ui/Hud';
import { Objectives } from './ui/Objectives';
import { beginPromptFrame, endPromptFrame } from './ui/Prompt';
import { WaypointMarker } from './ui/Waypoint';
import { buildCrashCoast } from './world/areas/a01/CrashCoast';
import { DeepJungle } from './world/areas/a02/DeepJungle';
import { resolveArea, Riverlands } from './world/areas/a03/Riverlands';
import { inPoisonVolume, SB_POISON_MULT, SwampOfBones } from './world/areas/a04/SwampOfBones';
import { VZ_HEAT_MULT, VolcanicZone } from './world/areas/a05/VolcanicZone';
import { Underground } from './world/areas/a06/Underground';
import { LostWorld } from './world/areas/a07/LostWorld';
import type { Ocean } from './world/Ocean';
import { nightBlend, tickPlume, tickSky } from './world/Sky';
import { Streamer } from './world/Streamer';
import { bindGlbDecoders } from './assets/loadGlb';

const VERSION = '0.0.1';

export type PauseReason = 'hidden' | 'menu' | 'blur' | 'dialogue';

export class Game {
  private readonly root: HTMLElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly quality: QualitySettings;
  private readonly debug: DsHook;
  private readonly clock = new Clock();
  private readonly pauseReasons = new Set<PauseReason>();
  private readonly physics: PhysicsWorld;
  private readonly streamer: Streamer;
  private readonly motor: CharacterMotor;
  private readonly locomotion = new Locomotion();
  private readonly input = new InputMap();
  private readonly boomBall: RAPIER.Ball;
  private readonly playerVisual: PlayerVisual;
  private readonly vitals = new Vitals();
  private readonly day = new DayClock();
  private readonly audio: AudioEngine;
  private readonly pickups: PickupDirector;
  private readonly camp: CampDirector;
  private readonly fauna: SpawnDirector;
  private readonly spear = new Spear();
  private readonly bow = new Bow();
  private readonly axe = new Axe();
  private readonly sling = new Sling();
  private readonly tools: ToolDirector;
  private readonly ocean: Ocean;
  private readonly jungle: DeepJungle;
  private readonly river: Riverlands;
  private readonly swamp: SwampOfBones;
  private readonly volcano: VolcanicZone;
  private readonly underground: Underground;
  private readonly lostWorld: LostWorld;
  private readonly worldEnd: Ending;
  private readonly harrow = new Pulse();
  private readonly playerLamp: THREE.SpotLight;
  private readonly playerFill: THREE.PointLight;
  private readonly viewLamp: THREE.SpotLight;
  private readonly wayMark: WaypointMarker;
  private lastHp = 100;
  private hurtFlash = 0;
  private combatLine = '';
  private bootLineUntil = 0;
  private readonly veil: DeathVeil;
  private readonly saves = new SaveGame();
  private readonly recipes: string[] = [];
  private flags = { knowMeridian: false, bowChip: false, boatReady: false };
  private saveAcc = 0;
  private readonly harvested = new Set<string>();
  private running = false;
  private lastTime = 0;
  private warmFrames = 0;
  private pulseHeard = false;

  static async boot(onProgress?: (msg: string) => void): Promise<Game> {
    onProgress?.('Starting physics…');
    await RAPIER.init();
    onProgress?.('Building Crash Coast…');
    const game = new Game();
    game.debug.rapierReady = true;
    game.debug.debug.rapierReady = true;
    game.bind();
    return game;
  }

  /** Compile shaders and pull hero GLBs while the splash is still up. */
  async warm(onProgress?: (msg: string) => void): Promise<void> {
    onProgress?.('First beach chunk…');
    const pos = this.motor.translation();
    this.streamer.update(pos.x, pos.z, 1);
    this.renderer.shadowMap.enabled = false;
    this.resize();
    this.renderer.render(this.scene, this.camera);
    onProgress?.('Ready — Start when you are');
  }

  private constructor() {
    const root = document.getElementById('game-root');
    if (!root) {
      throw new Error('Missing #game-root');
    }
    this.root = root;

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    bindGlbDecoders(this.renderer);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.42;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(55, 1, 0.1, 500);
    this.camera.position.set(10, 7, 14);
    this.camera.lookAt(0, 1, 0);
    this.scene.add(this.camera);

    this.physics = new PhysicsWorld();
    this.streamer = new Streamer(this.scene, this.physics);
    const built = buildCrashCoast(this.scene, this.physics);
    this.ocean = built.ocean;
    this.jungle = new DeepJungle();
    this.jungle.extendGround(this.physics);
    this.motor = new CharacterMotor(this.physics);
    this.motor.setTranslation(built.spawn.x, this.motor.centerY + 0.02, built.spawn.z);
    this.streamer.update(built.spawn.x, built.spawn.z, 1);
    this.boomBall = new RAPIER.Ball(BOOM_RADIUS);

    this.playerVisual = new PlayerVisual();
    this.playerLamp = new THREE.SpotLight(0xffe2b0, 0, 36, Math.PI / 3.1, 0.32, 0.7);
    this.playerLamp.name = 'ROWAN-LAMP';
    this.playerLamp.position.set(0.08, 1.58, -0.45);
    this.playerLamp.target.position.set(0, 0.6, -14);
    this.playerLamp.castShadow = false;
    this.playerFill = new THREE.PointLight(0xffc090, 0, 10, 1.1);
    this.playerFill.name = 'ROWAN-FILL';
    this.playerFill.position.set(0, 1.4, -1.4);
    this.playerFill.castShadow = false;
    this.playerVisual.add(this.playerLamp);
    this.playerVisual.add(this.playerLamp.target);
    this.playerVisual.add(this.playerFill);
    this.viewLamp = new THREE.SpotLight(0xffe8c4, 0, 48, Math.PI / 2.8, 0.38, 0.55);
    this.viewLamp.name = 'VIEW-LAMP';
    this.viewLamp.position.set(0.2, 0.05, 0.15);
    this.viewLamp.target.position.set(0, -0.6, -22);
    this.viewLamp.castShadow = false;
    this.camera.add(this.viewLamp);
    this.camera.add(this.viewLamp.target);
    this.wayMark = new WaypointMarker(this.scene);
    this.scene.add(this.playerVisual);
    this.pickups = new PickupDirector(this.scene);
    this.tools = new ToolDirector(this.pickups);
    this.river = new Riverlands(this.pickups.inventory);
    this.river.extendGround(this.physics);
    this.swamp = new SwampOfBones();
    this.swamp.extendGround(this.physics);
    this.volcano = new VolcanicZone();
    this.volcano.extendGround(this.physics);
    this.underground = new Underground();
    this.underground.extendGround(this.physics);
    this.lostWorld = new LostWorld();
    this.lostWorld.extendGround(this.physics);
    this.camp = new CampDirector(this.scene, this.pickups.inventory, this.vitals, this.day);
    this.fauna = new SpawnDirector(this.scene);
    this.worldEnd = new Ending({
      scene: this.scene,
      fauna: this.fauna,
      day: this.day,
      dinos: () => this.allDinos(),
    });

    this.quality = resolveQuality(this.renderer);
    applyQuality(this.renderer, this.scene, this.quality);

    this.root.appendChild(this.renderer.domElement);
    this.resize();

    const pos = this.motor.translation();
    this.debug = {
      tick: 0,
      paused: false,
      version: VERSION,
      boot: true,
      alpha: 0,
      dt: Clock.DT,
      rapierReady: true,
      player: { x: pos.x, y: pos.y, z: pos.z },
      yaw: 0,
      sneak: false,
      grounded: false,
      speed: 0,
      jumped: false,
      swimming: false,
      swimEntered: false,
      stamina: this.vitals.stamina,
      camInsideMesh: false,
      debug: {
        rapierReady: true,
        camInsideMesh: false,
        teleport: this.teleportPlayer,
        interact: this.debugInteract,
        give: this.giveItem,
        build: this.buildLeanTo,
        place: this.debugPlace,
        spawnCharge: this.debugSpawnCharge,
        pieces: [],
        attack: this.debugAttack,
        hurt: this.debugHurt,
        kill: this.debugKill,
        save: this.persistSave,
        load: this.debugLoad,
        unpause: this.debugUnpause,
        leanToUp: false,
        fireLit: false,
        pulse: { active: false, nextIn: 73, count: 0, inWindow: false, etaPlayS: 73 * (PLAY_S_PER_ISLAND_H / 60) },
        swimEntered: false,
        chunk: this.streamer.residentKeys(),
        residentCount: this.streamer.residentCount,
        navMode: this.fauna.nav.mode,
        fellThrough: false,
        quality: { ...this.quality },
        tier: this.quality.tier,
      },
      area: 'crash_coast',
      flags: {
        knowMeridian: false,
        bowChip: false,
        boatReady: false,
        keyA: false,
        keyB: false,
        keyC: false,
        enterCave: false,
        knowMatins: false,
        stampMatins: false,
        carryFlask: false,
      },
      ending: null,
      todMinutes: this.day.todMinutes,
      hp: this.vitals.hp,
      hunger: this.vitals.hunger,
      thirst: this.vitals.thirst,
      dead: this.vitals.dead,
      deathCause: null,
      deaths: 0,
      night: this.day.isNight,
      held: null,
      inventory: [],
      prompt: '',
      teleport: this.teleportPlayer,
      interact: this.debugInteract,
      give: this.giveItem,
      build: this.buildLeanTo,
      shelter: false,
      fireLit: false,
      lastDrink: null,
      dinos: [],
      blind: 0,
      attacked: 0,
      attacking: false,
      attack: this.debugAttack,
      hurt: this.debugHurt,
      kill: this.debugKill,
      save: this.persistSave,
      load: this.debugLoad,
    };
    window.__DS = this.debug;
    this.veil = new DeathVeil(this.stand);
    this.pickups.publish(this.debug);
    this.camp.publish(this.debug);
    this.fauna.publish(this.debug);
    this.audio = new AudioEngine(this.camera, this.debug);
    this.publishSurvival();
    Objectives.tick(this.debug);
    this.syncPlayerVisual();
    if (document.hidden) {
      this.pause('hidden');
    }
  }

  pause(reason: PauseReason, on = true): void {
    if (on) {
      this.pauseReasons.add(reason);
    } else {
      this.pauseReasons.delete(reason);
    }
    const paused = this.pauseReasons.size > 0;
    if (this.debug.paused && !paused) {
      this.lastTime = performance.now();
    }
    this.debug.paused = paused;
  }

  private bind(): void {
    window.addEventListener('resize', this.resize);
    document.addEventListener('visibilitychange', this.onVisibility);
    const start = document.getElementById('boot-start');
    start?.addEventListener('click', this.onStart, { once: true });
  }

  private readonly onStart = (): void => {
    const splash = document.getElementById('boot-splash');
    splash?.classList.add('hidden');
    splash?.setAttribute('aria-hidden', 'true');
    this.input.requestLock();
    this.bootLineUntil = performance.now() + 16_000;
    this.combatLine = 'KITE-6 — Rowan Hale, Crash Coast. Find the wreck spear.';
    Hud.show();
    void this.audio.unlock().then(() => {
      amb.start(this.audio);
    });
    this.running = true;
    this.lastTime = performance.now();
    requestAnimationFrame(this.frame);
  };

  private readonly onVisibility = (): void => {
    this.pause('hidden', document.hidden);
  };

  private readonly resize = (): void => {
    const width = Math.max(1, this.root.clientWidth);
    const height = Math.max(1, this.root.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  };

  private readonly frame = (): void => {
    if (!this.running) {
      return;
    }
    const now = performance.now();
    const elapsed = (now - this.lastTime) / 1000;
    this.lastTime = now;
    if (!this.debug.paused) {
      const cap = this.warmFrames < 45 ? 1 : 8;
      const ticks = Math.min(cap, this.clock.step(elapsed));
      for (let i = 0; i < ticks; i++) {
        this.tick();
      }
      this.debug.tick = this.clock.ticks;
    }
    this.debug.alpha = this.clock.alpha;
    this.debug.dt = this.clock.dt;
    const pos = this.motor.translation();
    this.streamer.update(pos.x, pos.z, 1);
    tickPlume(this.scene, now * 0.001);
    this.syncPlayerVisual();
    this.ocean.tick(now * 0.001);
    if (this.warmFrames < 50) {
      this.renderer.shadowMap.enabled = false;
      this.warmFrames += 1;
    } else if (this.warmFrames === 50) {
      this.renderer.shadowMap.enabled = true;
      this.warmFrames = 51;
    }
    this.renderer.render(this.scene, this.camera);
    requestAnimationFrame(this.frame);
  };

  private tick(): void {
    beginPromptFrame();
    const dead = this.vitals.dead;
    const look = this.input.consumeLook();
    if (!dead) {
      const slot = this.input.consumeHotbar();
      if (slot !== null) {
        this.pickups.equipSlot(slot);
      }
      if (this.input.consumeCycle()) {
        this.camp.cycle();
      }
      if (this.input.consumeCancel()) {
        this.camp.closeMenu();
      }
    }
    if (dead) {
      this.locomotion.halt();
      this.locomotion.integrateDriven(Clock.DT, this.motor, 0, 0);
    } else {
      this.locomotion.applyLook(look.x, look.y);
      this.locomotion.integrate(Clock.DT, this.input, this.motor, this.vitals);
    }
    this.physics.step();
    const pos = this.motor.translation();
    this.applyFall(pos);
    if (
      !dead &&
      this.locomotion.swimming &&
      this.locomotion.swim.emptyS >= DROWN_DELAY_S
    ) {
      this.vitals.hurt(DROWN_HP_PER_S * Clock.DT);
    }
    this.vitals.tick(Clock.DT, this.thirstMult(pos));
    if (this.vitals.hp < this.lastHp - 0.05) {
      this.hurtFlash = Math.min(28, this.lastHp - this.vitals.hp);
      if (!this.combatLine) {
        this.combatLine = 'You took a hit';
      }
    }
    this.lastHp = this.vitals.hp;
    this.hurtFlash = Math.max(0, this.hurtFlash - Clock.DT * 16);
    this.day.tick(Clock.DT);
    const dusk = this.day.lockNight ? 1 : nightBlend(this.day.todMinutes);
    tickSky(this.scene, this.day.lockNight ? 20 * 60 : this.day.todMinutes, pos);
    const torchLit = this.pickups.held === 'torch' && (this.day.isNight || dusk > 0.45);
    const lamp = dusk * 7 + (torchLit ? TORCH_LAMP_BOOST + 3 : dusk > 0.35 ? 2.4 : 0.35);
    this.playerLamp.intensity = lamp;
    this.playerFill.intensity = 1.1 + dusk * 2.4 + (torchLit ? 2.2 : 0);
    this.viewLamp.intensity = 0.55 + lamp * 0.85;
    this.harrow.sealed = this.debug.ending === 'seal';
    this.debug.debug.pulse = this.harrow.tick(Clock.DT, this.day.todMinutes);
    this.applyAreaLook(resolveArea(pos.x, pos.z, pos.y, this.debug.flags));
    this.updateCamera();
    const swing = !dead && this.input.consumeAttack();
    const drawing = !dead && (this.input.attackDown || this.input.aimDown);
    const ctx = this.spearCtx(pos);
    let swung = false;
    if (this.pickups.held === 'bow') {
      this.bow.drive(Clock.DT, drawing, ctx);
      if (swing && !drawing) {
        this.bow.tryFire(ctx);
      }
    } else if (this.pickups.held === 'sling') {
      this.sling.drive(Clock.DT, swing, ctx);
    } else if (this.pickups.held === 'axe' || this.pickups.held === 'metal_axe') {
      this.axe.drive(Clock.DT, swing, ctx);
    } else {
      swung = this.spear.drive(Clock.DT, swing, ctx);
      if (swing) {
        this.combatLine = this.spear.lastHit ? 'Hit' : ctx.held === 'spear' ? 'Miss' : '';
      }
    }
    const use = !dead && this.input.consumeInteract();
    const sense: FaunaSense = {
      hp: this.vitals.hp,
      held: this.pickups.held,
      sneak: this.locomotion.sneak,
      speed: this.locomotion.speed,
      swimming: this.locomotion.swimming,
      sprint: this.input.sprint,
      night: this.day.isNight,
      area: resolveArea(pos.x, pos.z, pos.y, this.debug.flags),
      camera: this.camera,
      interact: use,
      hurt: (n) => {
        this.vitals.hurt(n);
        this.combatLine = n >= 8 ? 'Bite / spit' : 'Peck';
      },
    };
    const speared = this.fauna.tick(Clock.DT, pos, sense);
    this.harvestKills();
    this.jungle.sync(this.scene, this.physics, pos.z);
    this.river.sync(this.scene, this.physics, pos.x);
    this.swamp.sync(this.scene, this.physics, pos.x);
    this.volcano.sync(this.scene, this.physics, pos.z);
    this.underground.sync(this.scene, this.physics, pos.y, pos.z);
    this.lostWorld.sync(this.scene, this.physics, pos.z, this.debug.flags);
    const leftover = use && !speared;
    const campUsed = this.camp.tick({
      camera: this.camera,
      player: pos,
      yaw: this.locomotion.yaw,
      dt: Clock.DT,
      use: leftover,
      build: !dead && this.input.consumeBuild(),
      fire: !dead && this.input.consumeFire(),
      interactDown: !dead && this.input.interactDown,
    });
    const jungleUsed = this.jungle.tick(this.camera, pos, leftover && !campUsed);
    const helix = this.jungle.takeCraft();
    const keyedCraft = !dead && this.input.consumeCraft();
    if (helix || keyedCraft) {
      if (craftBow(this.debug.flags, this.pickups) && !this.recipes.includes('bow')) {
        this.recipes.push('bow');
      } else if (keyedCraft && craftTorch(this.pickups) && !this.recipes.includes('torch')) {
        this.recipes.push('torch');
      }
    }
    this.swamp.tick(Clock.DT, pos, sense);
    this.volcano.tick(Clock.DT, pos, sense);
    if (
      leftover &&
      !this.lostWorld.ending &&
      this.volcano.tryIgnite(pos) &&
      this.lostWorld.knowMatins &&
      this.lostWorld.stampMatins
    ) {
      this.lostWorld.commit('burn');
    }
    const riverUsed = this.river.tick(Clock.DT, this.camera, pos, leftover && !campUsed && !jungleUsed, sense);
    const shaftUsed = this.underground.tick(
      Clock.DT,
      this.camera,
      pos,
      leftover && !campUsed && !jungleUsed && !riverUsed,
      sense,
    );
    const valeUsed = this.lostWorld.tick(
      Clock.DT,
      this.camera,
      pos,
      leftover && !campUsed && !jungleUsed && !riverUsed && !shaftUsed,
      sense,
      this.harrow.active,
    );
    this.worldEnd.apply(this.lostWorld.ending);
    this.worldEnd.tick(Clock.DT);
    this.camp.tickCharges(this.allDinos(), Clock.DT);
    const toolUsed = this.tools.tick(
      this.camera,
      pos,
      this.pickups.held,
      leftover && !campUsed && !jungleUsed && !riverUsed && !shaftUsed && !valeUsed,
    );
    this.pickups.tick(
      this.camera,
      pos,
      leftover && !campUsed && !jungleUsed && !riverUsed && !shaftUsed && !valeUsed && !toolUsed,
    );
    this.camp.syncPrompt(this.camera, pos);
    this.jungle.syncPrompt(this.camera, pos);
    this.river.syncPrompt(this.camera, pos);
    this.underground.syncPrompt(this.camera, pos, this.pickups.held);
    this.lostWorld.syncPrompt(this.camera, pos);
    this.tools.syncPrompt(pos, this.pickups.held);
    this.applyWarp();
    this.audioTick(this.debug.debug.pulse.active, swung);
    this.noteDeath();
    this.saveAcc += Clock.DT;
    if (this.saveAcc >= AUTO_SAVE_S || this.camp.takeSlept()) { this.saveAcc = 0; void this.persistSave(); }
    this.publishHook();
    this.wayMark.set(Objectives.waypoint());
    endPromptFrame();
  }

  private applyFall(pos: { x: number; y: number; z: number }): void {
    if (this.vitals.dead) {
      return;
    }
    const floor = this.debug.area === 'underground' ? -40 : -8;
    if (pos.y < floor) {
      this.vitals.kill('fall');
    }
  }

  private updateCamera(): void {
    const pos = this.motor.translation();
    const feetY = pos.y - this.motor.centerY;
    const result = applyCameraBoom(
      this.camera,
      this.physics.world,
      pos,
      feetY,
      this.locomotion.yaw,
      this.locomotion.pitch,
      this.locomotion.sneak,
      this.motor.body,
      this.boomBall,
    );
    this.debug.camInsideMesh = result.camInsideMesh;
    this.debug.debug.camInsideMesh = result.camInsideMesh;
  }

  private syncPlayerVisual(): void {
    const pos = this.motor.translation();
    this.playerVisual.position.set(pos.x, pos.y - this.motor.centerY + 0.04, pos.z);
    this.playerVisual.rotation.y = this.locomotion.yaw;
    this.playerVisual.setPose({
      sneak: this.locomotion.sneak,
      speed: this.locomotion.speed,
      yaw: this.locomotion.yaw,
      grounded: this.locomotion.grounded,
    });
    this.playerVisual.setHeld(this.pickups.held);
  }

  private publishHook(): void {
    const pos = this.motor.translation();
    this.debug.player.x = pos.x;
    this.debug.player.y = pos.y;
    this.debug.player.z = pos.z;
    this.debug.area = resolveArea(pos.x, pos.z, pos.y, this.debug.flags);
    this.debug.yaw = this.locomotion.yaw;
    this.debug.sneak = this.locomotion.sneak;
    this.debug.grounded = this.locomotion.grounded;
    this.debug.speed = this.locomotion.speed;
    this.debug.jumped = this.locomotion.jumped;
    this.debug.swimming = this.locomotion.swimming;
    this.debug.swimEntered = this.locomotion.swim.entered;
    this.debug.stamina = this.vitals.stamina;
    this.debug.debug.swimEntered = this.locomotion.swim.entered;
    this.debug.debug.chunk = this.streamer.residentKeys();
    this.debug.debug.residentCount = this.streamer.residentCount;
    this.debug.debug.navMode = this.fauna.nav.mode;
    this.debug.debug.fellThrough = pos.y < this.streamer.heightAt(pos.x, pos.z) - 2;
    this.debug.debug.playerClips = this.playerVisual.clipNames();
    this.debug.debug.playerClip = this.playerVisual.activeClip();
    this.debug.attacking = this.playerVisual.isAttacking();
    this.debug.debug.attacking = this.debug.attacking;
    const mem = this.renderer.info.memory;
    const draw = this.renderer.info.render;
    this.debug.debug.perf = {
      fps: this.debug.dt > 0 ? 1 / this.debug.dt : 0,
      calls: draw.calls,
      triangles: draw.triangles,
      geometries: mem.geometries,
      textures: mem.textures,
    };
    this.pickups.publish(this.debug);
    this.camp.publish(this.debug);
    this.fauna.publish(this.debug);
    this.jungle.publish(this.debug);
    this.river.publish(this.debug);
    this.swamp.publish(this.debug);
    this.volcano.publish(this.debug);
    this.underground.publish(this.debug);
    this.lostWorld.publish(this.debug);
    this.flags.knowMeridian = this.debug.flags.knowMeridian;
    this.flags.bowChip = this.debug.flags.bowChip;
    this.flags.boatReady = this.debug.flags.boatReady;
    this.debug.attacked = this.spear.attacked + this.bow.fired + this.axe.attacked + this.sling.fired;
    this.publishSurvival();
    Objectives.tick(this.debug);
  }

  private readonly teleportPlayer = (x: number, y: number, z: number): void => {
    this.motor.setTranslation(x, y, z);
    this.streamer.update(x, z);
    this.jungle.sync(this.scene, this.physics, z);
    this.river.sync(this.scene, this.physics, x);
    this.swamp.sync(this.scene, this.physics, x);
    this.volcano.sync(this.scene, this.physics, z);
    this.underground.sync(this.scene, this.physics, y, z);
    this.lostWorld.sync(this.scene, this.physics, z, this.debug.flags);
    this.applyAreaLook(resolveArea(x, z, y, this.debug.flags));
    this.applyFall(this.motor.translation());
    this.noteDeath();
    this.syncPlayerVisual();
    this.updateCamera();
    this.publishHook();
  };

  private readonly debugUnpause = (): void => {
    this.pauseReasons.clear();
    this.debug.paused = false;
    this.lastTime = performance.now();
  };

  private thirstMult(pos: { x: number; y: number; z: number }): number {
    const area = resolveArea(pos.x, pos.z, pos.y, this.debug.flags);
    if (area === 'volcanic') {
      return VZ_HEAT_MULT;
    }
    if (inPoisonVolume(pos.x, pos.z)) {
      return SB_POISON_MULT;
    }
    return 1;
  }

  private applyAreaLook(area: ReturnType<typeof resolveArea>): void {
    if (area === 'swamp') {
      this.swamp.applyFog(this.scene, this.day.isNight);
      return;
    }
    if (area === 'volcanic') {
      this.volcano.applyFog(this.scene, this.day.isNight);
      return;
    }
    if (area === 'underground') {
      this.underground.applyFog(this.scene, this.day.isNight);
      return;
    }
    if (area === 'lost_world') {
      this.lostWorld.applyFog(this.scene, this.day.isNight);
      return;
    }
    /* Coast / jungle / river fog is owned by tickSky so dusk can lerp. */
  }

  private readonly debugInteract = (): void => {
    const pos = this.motor.translation();
    if (
      !this.camp.forceUse(this.camera, pos) &&
      !this.jungle.forceUse(this.camera, pos) &&
      !this.river.forceUse(this.camera, pos) &&
      !this.underground.forceUse(this.camera, pos, this.pickups.held) &&
      !this.lostWorld.forceUse(this.camera, pos, this.harrow.active) &&
      !this.tools.forceUse(pos, this.pickups.held)
    ) {
      this.pickups.forceUse(this.camera, pos);
    }
    if (
      !this.lostWorld.ending &&
      this.volcano.tryIgnite(pos) &&
      this.lostWorld.knowMatins &&
      this.lostWorld.stampMatins
    ) {
      this.lostWorld.commit('burn');
    }
    if (this.jungle.takeCraft()) {
      craftBow(this.debug.flags, this.pickups);
    }
    this.applyWarp();
    this.worldEnd.apply(this.lostWorld.ending);
    this.publishHook();
  };

  private readonly giveItem = (id: string, n = 1): void => {
    if (id === 'boatReady') {
      this.river.grantBoat(n !== 0);
      this.publishHook();
      return;
    }
    if (id === 'keys') {
      this.underground.grantKeys();
      this.publishHook();
      return;
    }
    if (id === 'matins' || id === 'knowMatins' || id === 'stampMatins') {
      this.lostWorld.grantLore(id);
      this.publishHook();
      return;
    }
    if (id === 'pulse') {
      this.harrow.force();
      this.publishHook();
      return;
    }
    this.pickups.give(id, n);
    this.pickups.publish(this.debug);
  };

  private applyWarp(): void {
    const dest = this.underground.takeWarp();
    if (dest) {
      this.teleportPlayer(dest.x, dest.y, dest.z);
    }
  }

  private harvestKills(): void {
    for (const dino of this.allDinos()) {
      if (dino.live || this.harvested.has(dino.id)) {
        continue;
      }
      this.harvested.add(dino.id);
      const n = meatYield(dino.kind);
      this.pickups.dropRaw({ x: dino.x, y: dino.y, z: dino.z }, n);
      this.combatLine = n > 1 ? `Raw meat ×${n}` : 'Raw meat';
    }
  }

  private spearCtx(player = this.motor.translation()) {
    return { held: this.pickups.held, camera: this.camera, player, yaw: this.locomotion.yaw, dinos: this.allDinos(), stamina: this.vitals, visual: this.playerVisual };
  }

  private allDinos(): DinoActor[] {
    const list: DinoActor[] = [...this.fauna.actors];
    if (this.river.hunter) {
      list.push(this.river.hunter);
    }
    list.push(...this.river.extras);
    if (this.swamp.stego) {
      list.push(this.swamp.stego);
    }
    if (this.volcano.carno) {
      list.push(this.volcano.carno);
    }
    if (this.volcano.anky) {
      list.push(this.volcano.anky);
    }
    if (this.underground.hunter) {
      list.push(this.underground.hunter);
    }
    if (this.lostWorld.hunter) {
      list.push(this.lostWorld.hunter);
    }
    if (this.lostWorld.scout) {
      list.push(this.lostWorld.scout);
    }
    return list;
  }

  private readonly debugAttack = (): void => {
    this.spear.recover = 0;
    this.axe.recover = 0;
    if (this.pickups.held === 'bow') {
      this.bow.tryFire(this.spearCtx());
    } else if (this.pickups.held === 'sling') {
      this.sling.tryFire(this.spearCtx());
    } else if (this.pickups.held === 'axe' || this.pickups.held === 'metal_axe') {
      this.axe.tryAttack(this.spearCtx());
    } else if (this.spear.tryAttack(this.spearCtx())) {
      cues.play(this.audio, 'spear');
    }
    this.playerVisual.punch();
    this.combatLine = this.spear.lastHit ? 'Hit' : this.pickups.held === 'spear' ? 'Miss' : 'Swing';
    this.debug.attacking = true;
    this.publishHook();
  };

  private readonly debugHurt = (n: number): void => {
    this.vitals.hurt(n);
    this.noteDeath();
    this.publishHook();
  };

  private readonly debugKill = (): void => this.debugHurt(999);

  private audioTick(pulseActive: boolean, swung: boolean): void {
    amb.sync(this.audio, {
      night: this.day.isNight,
      fire: this.camp.fireLit,
      pulse: pulseActive,
    });
    if (pulseActive && !this.pulseHeard) {
      cues.play(this.audio, 'pulse');
    }
    this.pulseHeard = pulseActive;
    if (swung) {
      cues.play(this.audio, 'spear');
    }
    for (const dino of this.allDinos()) {
      if (dino.takeTell()) {
        cues.play(this.audio, 'diloph-tell', dino.mesh);
      }
      if (dino.takeSpit()) {
        cues.play(this.audio, 'diloph-spit', dino.mesh);
      }
    }
  }

  private noteDeath(): void {
    if (!this.vitals.dead || this.veil.shown) {
      return;
    }
    this.debug.deaths += 1;
    this.debug.deathCause = this.vitals.cause;
    this.veil.show(deathCopy(this.vitals.cause));
    cues.play(this.audio, 'death');
  }

  private readonly stand = (): void => {
    if (!this.vitals.dead) {
      return;
    }
    this.vitals.revive(REVIVE_HP, REVIVE_HUNGER, REVIVE_THIRST);
    const at = respawnAt(this.camp.shelter, this.camp.bed, this.motor.centerY);
    this.teleportPlayer(at.x, at.y, at.z);
    this.veil.hide();
    this.publishHook();
  };

  private readonly buildLeanTo = (): boolean => {
    const ok = this.camp.commitLeanTo(this.motor.translation(), this.locomotion.yaw);
    this.publishHook();
    return ok;
  };

  private readonly debugPlace = (kind: string): boolean => {
    const ok = this.camp.placePiece(kind, this.motor.translation(), this.locomotion.yaw);
    this.publishHook();
    return ok;
  };

  private readonly debugSpawnCharge = (): boolean => {
    const pos = this.motor.translation();
    const bed = this.camp.bed ?? { x: pos.x, z: pos.z };
    const x = bed.x + 1.2;
    const z = bed.z;
    const dino = this.fauna.spawn(`QA-TRIKE-${this.debug.tick}`, TRIKE_KIT, x, z, {
      minX: bed.x - 16,
      maxX: bed.x + 16,
      minZ: bed.z - 16,
      maxZ: bed.z + 16,
    });
    if (!dino) {
      return false;
    }
    dino.beginCharge(bed.x, bed.z);
    this.publishHook();
    return true;
  };

  private captureSave(): SaveV1 {
    const p = this.motor.translation();
    return {
      version: 1, x: p.x, y: p.y, z: p.z, yaw: this.locomotion.yaw,
      hp: this.vitals.hp, hunger: this.vitals.hunger, thirst: this.vitals.thirst,
      inventory: this.pickups.inventory.stacks(), recipes: this.recipes.slice(),
      flags: { ...this.debug.flags, ...(this.debug.ending ? { ending: this.debug.ending } : {}) }, shelter: this.camp.shelter, fireLit: this.camp.fireLit,
      deaths: this.debug.deaths, todMinutes: this.day.todMinutes,
    };
  }

  private applySave(data: SaveV1): void {
    this.teleportPlayer(data.x, data.y, data.z);
    this.locomotion.yaw = data.yaw;
    this.vitals.revive(data.hp, data.hunger, data.thirst);
    if (!this.vitals.dead) this.veil.hide();
    this.pickups.restore(data.inventory);
    this.recipes.splice(0, this.recipes.length, ...data.recipes);
    this.flags = {
      knowMeridian: Boolean(data.flags.knowMeridian),
      bowChip: Boolean(data.flags.bowChip),
      boatReady: Boolean(data.flags.boatReady),
    };
    this.jungle.restore(this.flags);
    this.river.restore(this.flags);
    this.underground.restore(data.flags);
    this.lostWorld.restore(data.flags);
    this.worldEnd.reset();
    this.worldEnd.apply(this.lostWorld.ending);
    this.camp.restore(data.shelter, data.fireLit);
    this.debug.deaths = data.deaths;
    this.day.skipTo(data.todMinutes);
    this.publishHook();
  }

  private readonly persistSave = (): Promise<boolean> => this.saves.save(this.captureSave());
  private readonly debugLoad = async (): Promise<boolean> => {
    const data = await this.saves.load();
    if (data) this.applySave(data);
    return !!data;
  };

  private publishSurvival(): void {
    const snap = this.vitals.snapshot(this.day);
    this.debug.todMinutes = snap.todMinutes;
    this.debug.hp = snap.hp;
    this.debug.hunger = snap.hunger;
    this.debug.thirst = snap.thirst;
    this.debug.dead = snap.dead;
    this.debug.deathCause = snap.cause;
    this.debug.night = snap.night;
    const dusk = this.day.lockNight ? 1 : nightBlend(snap.todMinutes);
    let notice = '';
    if (this.combatLine.startsWith('KITE-6') && performance.now() >= this.bootLineUntil) {
      this.combatLine = '';
    }
    if (this.combatLine) {
      notice = this.combatLine;
    } else if (this.pickups.lastTake) {
      notice = `Took ${this.pickups.lastTake}`;
    } else if (this.pickups.held === 'tracker') {
      const ping = nearestTracked(this.debug.player, this.debug.dinos ?? []);
      notice = ping ? `${ping.taxon} · ${Math.round(ping.dist)} m` : 'No dinosaurs nearby';
    } else if (this.debug.debug.pulse.active) {
      notice = 'The Harrow pulse';
    } else if (this.debug.blind > 0) {
      notice = 'Diloph spit — vision burned';
    } else if (dusk > 0.12 && dusk < 0.95 && !this.day.lockNight) {
      notice = 'Light is leaving Vesper Cay';
    }
    Hud.set({
      hp: snap.hp,
      hunger: snap.hunger,
      thirst: snap.thirst,
      todMinutes: snap.todMinutes,
      blind: this.debug.blind,
      notice,
      hurt: this.hurtFlash,
      build: this.camp.recipeLine(),
      swing: this.playerVisual.isAttacking(),
      reticle: true,
    });
  }
}
